import { useCallback } from "react"
import { camelToSnakeCase } from "utils/stringUtils"

/**
 * Custom hook for handling table changes (sort, filter, search, pagination)
 * @param {Function} onTableChange - Function to call when table state changes
 * @returns {Object} Object containing handler functions for different table changes
 */
const useTableChangeHandler = (onTableChange) => {
  const handleSort = useCallback(
    (value) => {
      onTableChange({
        sort: {
          field: camelToSnakeCase(value.column),
          order: value.direction,
        },
      })
    },
    [onTableChange]
  )

  const handleFilter = useCallback(
    (value) => {
      // Convert filter keys to snake_case
      const snakeCaseFilters = Object.entries(value).reduce((acc, [key, val]) => {
        acc[camelToSnakeCase(key)] = val;
        return acc;
      }, {});

      onTableChange({
        filters: snakeCaseFilters,
      })
    },
    [onTableChange]
  )

  const handleSearch = useCallback(
    (value) => {
      onTableChange({
        search: value,
      })
    },
    [onTableChange]
  )

  const handlePageChange = useCallback(
    (value) => {
      onTableChange({
        pagination: {
          page: value.page,
          limit: value.itemsPerPage,
        },
      })
    },
    [onTableChange]
  )

  const handleDateRange = useCallback(
    (value) => {
      onTableChange({
        dateRange: value,
      })
    },
    [onTableChange]
  )

  // For backward compatibility or when you need a generic handler
  const handleChange = useCallback(
    (type, value) => {
      switch (type) {
        case "sort":
          handleSort(value)
          break
        case "filter":
          handleFilter(value)
          break
        case "search":
          handleSearch(value)
          break
        case "pagination":
          handlePageChange(value)
          break
        case "dateRange":
          handleDateRange(value)
          break
        default:
          break
      }
    },
    [handleSort, handleFilter, handleSearch, handlePageChange, handleDateRange]
  )

  // Function to force refresh the table data
  const forceRefresh = useCallback(() => {
    if (onTableChange) {
      onTableChange({
        _t: Date.now(), // เพิ่ม timestamp เพื่อบังคับ refetch
      });
    }
  }, [onTableChange]);

  return {
    handleSort,
    handleFilter,
    handleSearch,
    handlePageChange,
    handleDateRange,
    handleChange, 
    forceRefresh, // Function to force refresh the table data
  }
}

export default useTableChangeHandler
